import { useState } from 'react';
import { LogEntry } from '../types';
import { generateId } from '../utils/helpers';

interface TransactionLogEntry extends LogEntry {
  tx_hash?: string;
  explorer_url?: string;
}

export const useTransactionLogs = () => {
  const [logs, setLogs] = useState<TransactionLogEntry[]>([]);

  // Функция для фильтрации DCA логов из терминала
  const parseDCAFromTerminal = (message: string): TransactionLogEntry | null => {
    // Исключаем детальные логи, которые не нужны на сайте
    const excludePatterns = [
      /HTTP\/1\.1.*200 OK/,  // HTTP запросы
      /Баланс\s+(ETH|токена):\s+[\d.]+/,  // Детальные балансы
      /Balance\s+loaded\s+for\s+wallet/,  // Логи загрузки баланса
      /Strategy\s+\w+\s+executed\s+transaction/,  // Логи выполнения транзакций
      /DCA\s+strategy\s+started\s+for\s+wallet/,  // Логи запуска стратегии
      /Начало\s+(покупки|продажи):\s+[\d.]+/,  // Детали операций
      /Котировка\s+(покупки|продажи):\s+[\d.]+/,  // Котировки
      /Одобрение\s+токена\s+для\s+swap/,  // Одобрение токенов
      /Gas\s+для\s+(покупки|продажи):\s+[\d.]+/,  // Детали газа
      /Gas:\s+[\d.]+.*Limit:\s+\d+.*Стоимость:/,  // Детали газа
      /Фактический\s+gas:\s+\d+.*Стоимость:/,  // Фактический газ
      /Etherscan:\s+https:\/\/etherscan\.io/,  // Ссылки на Etherscan
    ];

    // Проверяем, нужно ли исключить это сообщение
    const shouldExclude = excludePatterns.some(pattern => pattern.test(message));
    if (shouldExclude) {
      return null;
    }

    // Ищем только основные паттерны DCA логов
    const dcaPatterns = [
      // Паттерн: [task_id] Операция #N (транзакций: X/Y)
      /\[([^\]]+)\]\s+Операция\s+#(\d+)\s+\(транзакций:\s+(\d+)\/(\d+)\)/,
      // Паттерн: [task_id] Покупка за X ETH
      /\[([^\]]+)\]\s+Покупка\s+за\s+([\d.]+)\s+ETH/,
      // Паттерн: [task_id] Продажа X токенов
      /\[([^\]]+)\]\s+Продажа\s+([\d.]+)\s+токенов/,
      // Паттерн: Транзакция отправлена: 0x...
      /Транзакция\s+отправлена:\s+(0x[a-fA-F0-9]{64})/,
      // Паттерн: Транзакция подтверждена: 0x...
      /Транзакция\s+подтверждена:\s+(0x[a-fA-F0-9]{64})/,
      // Паттерн: Покупка выполнена успешно: 0x...
      /Покупка\s+выполнена\s+успешно:\s+(0x[a-fA-F0-9]{64})/,
      // Паттерн: Продажа выполнена успешно: 0x...
      /Продажа\s+выполнена\s+успешно:\s+(0x[a-fA-F0-9]{64})/,
      // Паттерн: [task_id] Ожидание X секунд...
      /\[([^\]]+)\]\s+Ожидание\s+(\d+)\s+секунд/,
      // Паттерн: [task_id] Остановлен
      /\[([^\]]+)\]\s+Остановлен/,
      // Паттерн: [task_id] СТОП: ...
      /\[([^\]]+)\]\s+СТОП:\s+(.+)/,
      // Паттерн: [task_id] ERROR: ...
      /\[([^\]]+)\]\s+ERROR:\s+(.+)/,
      // Паттерн: [task_id] Запуск DCA ...
      /\[([^\]]+)\]\s+Запуск\s+DCA\s+(\w+)/,
      // Паттерн: [task_id] Кошелек: 0x...
      /\[([^\]]+)\]\s+Кошелек:\s+(0x[a-fA-F0-9]{40})/,
      // Паттерн: [task_id] Диапазон: X-Y
      /\[([^\]]+)\]\s+Диапазон:\s+([\d.]+)-([\d.]+)/,
      // Паттерн: [task_id] Интервал: X сек
      /\[([^\]]+)\]\s+Интервал:\s+(\d+)\s+сек/,
    ];

    // Проверяем, содержит ли сообщение DCA-связанную информацию
    const isDCARelated = dcaPatterns.some(pattern => pattern.test(message));
    
    if (!isDCARelated) {
      return null;
    }

    // Определяем уровень лога
    let level: LogEntry['level'] = 'info';
    if (message.includes('ERROR') || message.includes('СТОП') || message.includes('ошибка')) {
      level = 'error';
    } else if (message.includes('успешно') || message.includes('подтверждена') || message.includes('выполнена')) {
      level = 'success';
    } else if (message.includes('предупреждение') || message.includes('WARNING')) {
      level = 'warning';
    }

    // Извлекаем task_id если есть
    const taskIdMatch = message.match(/\[([^\]]+)\]/);
    const taskId = taskIdMatch ? taskIdMatch[1] : undefined;

    // Извлекаем хеш транзакции если есть
    const txHashMatch = message.match(/0x[a-fA-F0-9]{64}/);
    const txHash = txHashMatch ? txHashMatch[0] : undefined;

    return {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      message: message.trim(),
      strategy: taskId,
      tx_hash: txHash,
      explorer_url: txHash ? `https://etherscan.io/tx/${txHash}` : undefined
    };
  };

  // Функция для добавления лога
  const addTransactionLog = (level: LogEntry['level'], message: string, strategy?: string, tx_hash?: string, explorer_url?: string) => {
    const newLog: TransactionLogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      message,
      strategy,
      tx_hash,
      explorer_url,
    };
    
    setLogs(prevLogs => {
      const updatedLogs = [...prevLogs, newLog];
      // Ограничиваем количество логов
      if (updatedLogs.length > 500) {
        return updatedLogs.slice(-500);
      }
      return updatedLogs;
    });
  };

  // Функция для парсинга логов из сообщений бэкенда
  const parseBackendMessage = (message: string, strategy?: string) => {
    // Определяем уровень лога по содержимому
    let level: LogEntry['level'] = 'info';
    if (message.includes('✅') || message.toLowerCase().includes('успешно') || message.toLowerCase().includes('отправлена')) {
      level = 'success';
    } else if (message.includes('❌') || message.toLowerCase().includes('ошибка') || message.toLowerCase().includes('провалилась')) {
      level = 'error';
    } else if (message.includes('⚠️') || message.toLowerCase().includes('предупреждение')) {
      level = 'warning';
    }

    // Извлекаем tx_hash и explorer_url если есть
    let tx_hash: string | undefined;
    let explorer_url: string | undefined;

    if (message.includes('TX Hash:')) {
      const parts = message.split('TX Hash: ');
      if (parts.length > 1) {
        tx_hash = parts[1].trim();
      }
    }

    if (message.includes('Explorer:') || message.includes('https://')) {
      const urlMatch = message.match(/https:\/\/[^\s]+/);
      if (urlMatch) {
        explorer_url = urlMatch[0];
      }
    }

    addTransactionLog(level, message, strategy, tx_hash, explorer_url);
  };

  // Функция для симуляции получения логов от бэкенда
  // В реальном приложении это может быть WebSocket или периодические запросы
  const simulateBackendLogs = (strategy_id: string, step: string, details?: any) => {
    // Создаем сообщения в формате терминала
    let terminalMessages: string[] = [];
    
    switch (step) {
      case 'start_execution':
        terminalMessages = [
          `[${strategy_id}] Запуск DCA ${details.action?.toUpperCase() || 'BUY'}`,
          `[${strategy_id}] Кошелек: ${details.wallet_address || '0x...'}`,
          `[${strategy_id}] Диапазон: ${details.min_amount || '0.01'}-${details.max_amount || '0.02'}`,
          `[${strategy_id}] Интервал: ${details.interval || 300} сек`
        ];
        break;
      case 'operation_start':
        terminalMessages = [
          `[${strategy_id}] Операция #${details.execution || 1} (транзакций: ${details.executed || 0}/${details.total || 0})`,
          `[${strategy_id}] ${details.action === 'buy' ? 'Покупка' : 'Продажа'} за ${details.amount} ${details.action === 'buy' ? 'ETH' : 'токенов'}`
        ];
        break;
      case 'balance_check':
        // Не показываем детальные балансы
        terminalMessages = [];
        break;
      case 'quote_received':
        // Не показываем детальные котировки
        terminalMessages = [];
        break;
      case 'transaction_sent':
        terminalMessages = [
          `Транзакция отправлена: ${details.tx_hash}`
        ];
        break;
      case 'transaction_confirmed':
        terminalMessages = [
          `Транзакция подтверждена: ${details.tx_hash}`
        ];
        break;
      case 'transaction_success':
        terminalMessages = [
          `${details.action === 'buy' ? 'Покупка' : 'Продажа'} выполнена успешно: ${details.tx_hash}`
        ];
        break;
      case 'waiting':
        terminalMessages = [
          `[${strategy_id}] Ожидание ${details.interval || 300} секунд...`
        ];
        break;
      case 'strategy_stopped':
        terminalMessages = [
          `[${strategy_id}] Остановлен`
        ];
        break;
      case 'strategy_error':
        terminalMessages = [
          `[${strategy_id}] ERROR: ${details.error_message || 'Неизвестная ошибка'}`,
          `[${strategy_id}] СТОП: ${details.error_message || 'Неизвестная ошибка'}`
        ];
        break;
      default:
        terminalMessages = [`[${strategy_id}] ${step}`];
    }

    // Обрабатываем каждое сообщение через фильтр DCA логов
    terminalMessages.forEach(message => {
      const dcaLog = parseDCAFromTerminal(message);
      if (dcaLog) {
        addTransactionLog(dcaLog.level, dcaLog.message, dcaLog.strategy, dcaLog.tx_hash, dcaLog.explorer_url);
      }
    });
  };

  // Функция для очистки логов
  const clearLogs = () => {
    setLogs([]);
  };

  return {
    logs,
    addTransactionLog,
    parseBackendMessage,
    simulateBackendLogs,
    clearLogs,
  };
};
